// 0 件になったときの逃げ道パネル。「どの条件が効きすぎているか」を件数で見せ、
// 1 タップで外せるようにする。DOM に触らない純粋な文字列組み立てなので、
// 描画（main.ts）と切り離してテストできる。
import type { ConditionKey, FilterState, HubKey, RelaxOption } from "../filters";
import { choiceLabel, type FilterChoiceField } from "../filterChoices";
import { t, type Lang } from "../i18n";
import { getMetrics, type MetricKey } from "../metrics";
import { prefLabel } from "../prefNames";
import { escapeHtml } from "../ssr";

/** ボタンが増えすぎると選べなくなるので、効果の大きい順にこの数だけ出す。 */
export const ZERO_RELAX_MAX = 6;

/** 数値の条件は「指標の短縮名 + 選択肢のラベル」で書ける。表に無いキーだけを個別に扱う。 */
const CONDITION_METRIC: Partial<Record<ConditionKey, MetricKey>> = {
  floodMax: "flood",
  tsunamiMax: "tsunami",
  sedimentMax: "sediment",
  quakeMax: "quake",
  landPriceMax: "landPrice",
  populationMin: "population",
  tempJanMin: "tempJan",
  tempAugMax: "tempAug",
};

function hubName(hub: HubKey, lang: Lang): string {
  const m = t(lang);
  return { tokyo: m.hubTokyo, osaka: m.hubOsaka, nagoya: m.hubNagoya }[hub];
}

/** 条件 1 つを人が読める文にする（「洪水 5% 以下」「大阪 120 km 以内」）。 */
export function conditionLabel(key: ConditionKey, filters: FilterState, lang: Lang): string {
  const m = t(lang);

  if (key === "pref") return prefLabel(filters.pref ?? "", lang);
  if (key === "tsunamiInlandOnly") return `${getMetrics(lang).tsunami.short} ${m.tsunamiInlandShort}`;
  if (key === "excludeUnmeasured") return m.unmeasuredExclude;
  if (key === "hubDistMax") {
    const value = filters.hubDistMax ?? 0;
    return `${hubName(filters.hub, lang)} ${choiceLabel("hubDistMax", value, lang)}`;
  }

  const metric = CONDITION_METRIC[key];
  const value = filters[key];
  if (metric === undefined || typeof value !== "number") return String(value ?? "");
  return `${getMetrics(lang)[metric].short} ${choiceLabel(key as FilterChoiceField, value, lang)}`;
}

function relaxButton(option: RelaxOption, filters: FilterState, lang: Lang): string {
  const label = t(lang).zeroResultRelax(conditionLabel(option.key, filters, lang), option.count);
  return `<button type="button" class="pill zero-relax" data-relax="${escapeHtml(option.key)}" data-relax-count="${option.count}">${escapeHtml(label)}</button>`;
}

/**
 * 0 件のときだけ結果一覧の先頭に差し込む `<li>`。件数が戻る条件が 1 つも無ければ
 * ボタンを並べても行き止まりなので、文言を変えて「すべて解除」だけに絞る。
 */
export function zeroResultHtml(options: RelaxOption[], filters: FilterState, lang: Lang): string {
  const m = t(lang);
  const usable = options.filter((o) => o.count > 0).slice(0, ZERO_RELAX_MAX);
  const lead =
    usable.length > 0 ? m.zeroResultLead : options.length > 0 ? m.zeroResultNoSingle : m.zeroResultNoCondition;

  const buttons = usable.map((o) => relaxButton(o, filters, lang)).join("");
  const clearAll =
    options.length > 0
      ? `<button type="button" class="pill zero-relax zero-relax-all" data-relax="all">${escapeHtml(m.zeroResultClearAll)}</button>`
      : "";
  const actions = buttons || clearAll ? `<div class="zero-result-actions">${buttons}${clearAll}</div>` : "";

  return `<li class="zero-result"><p class="zero-result-lead">${escapeHtml(lead)}</p>${actions}</li>`;
}
